import type { ClientListQuery } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useState } from "react"

import { useFreshTwoFactor } from "../../../access/fresh-two-factor"
import { requestClientsExport } from "../../../api/clients.api"
import { type ApiProblem, isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { Button, TextField } from "../../../ui"
import styles from "./export-dialog.module.css"

/**
 * «Выгрузить выборку» (ТЗ 03, §3.5): массовая выгрузка — НЕ кнопка «Экспорт».
 *
 * Цель выгрузки обязательна: она попадает в журнал действий и в разбор, если база всплывёт.
 * Перед запуском сервер требует свежий второй фактор — окно подтверждения появляется
 * само (useFreshTwoFactor), и после верного кода запрос повторяется без повторного ввода.
 * Квота — 500 карточек в сутки на сотрудника (решение владельца 11.10.2026).
 *
 * ПОКА ХРАНИЛИЩЕ ФАЙЛОВ НЕ ПОДКЛЮЧЕНО, сервер честно отвечает «выгрузка недоступна» —
 * уже после проверки права, фактора и квоты. Экран говорит это прямо: файл не собран,
 * данные никуда не ушли.
 */
export function ExportDialog(props: {
  /** Фильтр текущей выборки; поля без значения не уходят вовсе. */
  readonly filter: Readonly<Record<string, string | number | boolean | undefined>>
  readonly countLabel: string | null
  readonly onClose: () => void
}): ReactNode {
  const { run, dialog } = useFreshTwoFactor()
  const [purpose, setPurpose] = useState("")
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<{ readonly tone: "ok" | "error"; readonly text: string } | null>(null)
  const [showError, setShowError] = useState(false)

  const purposeError = purpose.trim().length < 3 ? "Напишите, зачем выгрузка: это увидит владелец в журнале действий" : null

  const submit = async (): Promise<void> => {
    setShowError(true)
    if (purposeError !== null) return
    setBusy(true)
    setOutcome(null)
    try {
      const job = await run(() =>
        requestClientsExport({
          filter: {
            ...Object.fromEntries(Object.entries(props.filter).filter(([, value]) => value !== undefined)),
            includeMerged: false,
          } as ClientListQuery,
          format: "csv",
          purpose: purpose.trim(),
        }),
        "вы выгружаете из салона персональные данные клиентов",
      )
      setOutcome({
        tone: "ok",
        text:
          job.status === "ready"
            ? "Файл готов — ссылка действует ограниченное время."
            : "Выгрузка поставлена в очередь. Когда файл будет готов, ссылка появится здесь и в центре уведомлений.",
      })
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setOutcome({ tone: "error", text: exportProblemText(error) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <JournalDialog title="Выгрузить выборку" onClose={props.onClose}>
        <div className={styles.body}>
          <p className={styles.text}>
            {props.countLabel === null ? "Выгрузятся клиенты текущей выборки." : `Выгрузятся: ${props.countLabel}.`} Файл
            содержит персональные данные; не больше 500 карточек в сутки, ссылка на файл живёт ограниченное время.
          </p>
          <TextField
            label="Зачем выгружаем"
            required
            value={purpose}
            maxLength={300}
            placeholder="Например: сверка базы перед переездом кассы"
            error={showError ? (purposeError ?? undefined) : undefined}
            onChange={(event) => setPurpose(event.target.value)}
          />
          {outcome === null ? null : (
            <div className={outcome.tone === "ok" ? styles.outcome : styles.problem} role={outcome.tone === "ok" ? "status" : "alert"}>
              {outcome.text}
            </div>
          )}
          <div className={styles.buttons}>
            <Button onClick={props.onClose}>{outcome?.tone === "ok" ? "Готово" : "Отмена"}</Button>
            {outcome?.tone === "ok" ? null : (
              <Button variant="primary" loading={busy} onClick={() => void submit()}>
                Выгрузить
              </Button>
            )}
          </div>
        </div>
      </JournalDialog>
      {dialog}
    </>
  )
}

/** Отказ выгрузки словами: что случилось, почему, что делать. */
export function exportProblemText(problem: ApiProblem): string {
  switch (problem.code) {
    case "export_unavailable":
      return "Выгрузка заработает, когда салону подключат хранилище файлов. Файл не собран, данные никуда не ушли."
    case "two_factor_stale":
    case "mfa_reconfirm_required":
      return "Выгрузка не запущена: личность не подтверждена вторым фактором."
    case "offline":
    case "timeout":
      return "Нет связи: выгрузка не запущена. Повторите, когда связь вернётся."
    default:
      return problem.detail ?? problem.title
  }
}
